import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  TextField,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  Badge,
  Tooltip,
  Card,
  Grid,
  alpha,
  useMediaQuery,
  useTheme,
  Paper,
  Pagination,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import SendIcon from "@mui/icons-material/Send";
import PeopleIcon from "@mui/icons-material/People";
import RefreshIcon from "@mui/icons-material/Refresh";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import chatService from "API/chatService";
import { useSignalR } from "hooks/useSignalR";
import signalRManager from "services/signalRManager";
import { API_ENDPOINTS, getFullUrl } from "config/api";
import safeToast from "utils/safeToast";
import CryptoJS from "crypto-js";

const AES_KEY = "7D3B8E69F1A84B7EA8A3F6792B44C926";
const AES_IV = "A1B2C3D4E5F60718";

const decryptText = (encryptedText) => {
  const key = CryptoJS.enc.Utf8.parse(AES_KEY);
  const iv = CryptoJS.enc.Utf8.parse(AES_IV);
  const decrypted = CryptoJS.AES.decrypt(encryptedText, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  return decrypted.toString(CryptoJS.enc.Utf8);
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};

const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const formatDateHeader = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const messageDate = new Date(date);
  messageDate.setHours(0, 0, 0, 0);
  
  if (messageDate.getTime() === today.getTime()) {
    return "Today";
  } else if (messageDate.getTime() === yesterday.getTime()) {
    return "Yesterday";
  } else {
    return messageDate.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: messageDate.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
    });
  }
};

const isSameDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);
  return d1.getTime() === d2.getTime();
};

const typingAnimation = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  30% {
    transform: translateY(-8px);
    opacity: 1;
  }
`;

const AdminGlobalChatDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [chatStats, setChatStats] = useState(null);
  const [adminInfo, setAdminInfo] = useState(null);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editMessageText, setEditMessageText] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioChunks, setAudioChunks] = useState([]);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [onlineUsersModalOpen, setOnlineUsersModalOpen] = useState(false);
  const [allOnlineUsers, setAllOnlineUsers] = useState([]);
  const [onlineUsersPage, setOnlineUsersPage] = useState(1);
  const [onlineUsersTotalCount, setOnlineUsersTotalCount] = useState(0);
  const [onlineUsersTotalPages, setOnlineUsersTotalPages] = useState(0);
  const [loadingOnlineUsers, setLoadingOnlineUsers] = useState(false);
  const onlineUsersPageSize = 50;
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioRefs = useRef({});

  const {
    isConnected,
    messages,
    setMessages,
    onlineUsers,
    typingUsers,
    sendMessage,
    startTyping,
    stopTyping,
    connection,
  } = useSignalR();

  // Load admin info
  useEffect(() => {
    if (open) {
      const adminSession = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
      if (adminSession) {
        try {
          const decrypted = decryptText(adminSession);
          const parsed = JSON.parse(decrypted);
          setAdminInfo({
            staffCode: parsed.StaffCode || parsed.staffCode || "Admin",
            staffId: parsed.StaffId || parsed.staffId,
          });
        } catch (e) {
          console.error("Error parsing admin session:", e);
        }
      }
    }
  }, [open]);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Load chat history
  const loadChatHistory = useCallback(async (page = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await chatService.getChatHistory(page, 50);
      
      if (append) {
        setChatHistory((prev) => [...(response.messages || []), ...prev]);
      } else {
        setChatHistory(response.messages || []);
      }
      
      setTotalMessages(response.totalCount || 0);
      setPageNumber(page);

      if (!append) {
        setTimeout(() => scrollToBottom(), 100);
      }
    } catch (error) {
      safeToast.error("Failed to load chat history");
      console.error("Error loading chat history:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Load more messages
  const loadMoreMessages = useCallback(async () => {
    if (loadingMore || loading) return;
    const nextPage = pageNumber + 1;
    const totalPages = Math.ceil(totalMessages / 50);
    if (nextPage > totalPages) return;

    await loadChatHistory(nextPage, true);
  }, [pageNumber, totalMessages, loadingMore, loading, loadChatHistory]);

  // Load chat stats
  const loadChatStats = useCallback(async () => {
    try {
      const stats = await chatService.getChatStats();
      setChatStats(stats);
    } catch (error) {
      console.error("Error loading chat stats:", error);
    }
  }, []);

  // Load paginated online users
  const loadPaginatedOnlineUsers = useCallback(async (page = 1) => {
    try {
      setLoadingOnlineUsers(true);
      const response = await chatService.getOnlineUsers(page, onlineUsersPageSize);
      if (response.onlineUsers) {
        // Backward compatibility - non-paginated response
        setAllOnlineUsers(response.onlineUsers || []);
        setOnlineUsersTotalCount(response.count || response.onlineUsers?.length || 0);
        setOnlineUsersTotalPages(1);
      } else {
        // Paginated response
        setAllOnlineUsers(response.onlineUsers || []);
        setOnlineUsersTotalCount(response.totalCount || 0);
        setOnlineUsersTotalPages(response.totalPages || 1);
      }
    } catch (error) {
      console.error("Error loading online users:", error);
      safeToast.error("Failed to load online users");
      setAllOnlineUsers([]);
    } finally {
      setLoadingOnlineUsers(false);
    }
  }, [onlineUsersPageSize]);

  // Handle open online users modal
  const handleOpenOnlineUsersModal = useCallback(async () => {
    setOnlineUsersModalOpen(true);
    setOnlineUsersPage(1);
    await loadPaginatedOnlineUsers(1);
  }, [loadPaginatedOnlineUsers]);

  // Handle close online users modal
  const handleCloseOnlineUsersModal = useCallback(() => {
    setOnlineUsersModalOpen(false);
    setAllOnlineUsers([]);
    setOnlineUsersPage(1);
  }, []);

  // Handle page change in online users modal
  const handleOnlineUsersPageChange = useCallback(async (event, newPage) => {
    setOnlineUsersPage(newPage);
    await loadPaginatedOnlineUsers(newPage);
  }, [loadPaginatedOnlineUsers]);

  // Initial load
  useEffect(() => {
    if (open) {
      loadChatHistory();
      loadChatStats();
    }
  }, [open, loadChatHistory, loadChatStats]);

  // Update chat history when new messages arrive via SignalR
  useEffect(() => {
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      setChatHistory((prev) => {
        if (prev.some((m) => m.messageId === latestMessage.messageId)) {
          return prev;
        }
        return [...prev, latestMessage];
      });
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [messages]);

  // Handle SignalR message events
  useEffect(() => {
    if (!connection) return;

    const handleMessageDeleted = (messageId) => {
      setChatHistory((prev) => prev.filter((m) => m.messageId !== messageId));
    };

    const handleMessageUpdated = (updatedMessage) => {
      setChatHistory((prev) =>
        prev.map((m) =>
          m.messageId === updatedMessage.messageId ? updatedMessage : m
        )
      );
    };

    connection.on("MessageDeleted", handleMessageDeleted);
    connection.on("MessageUpdated", handleMessageUpdated);

    return () => {
      connection.off("MessageDeleted", handleMessageDeleted);
      connection.off("MessageUpdated", handleMessageUpdated);
    };
  }, [connection]);

  // Auto-scroll
  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, typingUsers]);

  // Handle send message
  const handleSendMessage = async () => {
    if ((!message.trim() && !selectedImage) || sending || !isConnected) return;

    if (typingTimeout) {
      clearTimeout(typingTimeout);
      setTypingTimeout(null);
    }
    stopTyping();

    setSending(true);
    let imageUrl = null;
    let audioUrl = null;

    try {
      if (selectedImage) {
        setUploadingImage(true);
        const isAudio = selectedImage.type?.startsWith("audio/");
        if (isAudio) {
          const uploadResult = await chatService.uploadVoice(selectedImage);
          audioUrl = uploadResult.audioUrl;
        } else {
          const uploadResult = await chatService.uploadImage(selectedImage);
          imageUrl = uploadResult.imageUrl;
        }
        setUploadingImage(false);
      }

      if (isConnected && connection) {
        await signalRManager.sendMessage(message.trim() || "", imageUrl, audioUrl);
      } else {
        const response = await fetch(API_ENDPOINTS.CHAT.SEND_MESSAGE, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${chatService.getAdminToken()}`,
          },
          body: JSON.stringify({
            message: message.trim() || "",
            imageUrl: imageUrl,
            audioUrl: audioUrl,
          }),
        });
        if (!response.ok) throw new Error("Failed to send message");
      }

      setMessage("");
      handleRemoveImage();
      inputRef.current?.focus();
    } catch (error) {
      safeToast.error("Failed to send message");
      console.error("Error sending message:", error);
      setUploadingImage(false);
    } finally {
      setSending(false);
    }
  };

  // Handle image/audio selection
  const handleImageSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isAudio = file.type?.startsWith("audio/");
    const isImage = file.type?.startsWith("image/");

    if (isAudio) {
      const allowedAudioTypes = ["audio/webm", "audio/mp3", "audio/wav", "audio/ogg", "audio/m4a"];
      if (!allowedAudioTypes.includes(file.type)) {
        safeToast.error("Invalid audio type. Allowed: WEBM, MP3, WAV, OGG, M4A");
        return;
      }
      const maxAudioSize = 10 * 1024 * 1024; // 10MB for audio
      if (file.size > maxAudioSize) {
        safeToast.error("Audio size exceeds 10MB limit");
        return;
      }
    } else if (isImage) {
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        safeToast.error("Invalid file type. Please select an image (JPG, PNG, GIF, WEBP)");
        return;
      }
      const maxSize = 5 * 1024 * 1024; // 5MB for images
      if (file.size > maxSize) {
        safeToast.error("Image size exceeds 5MB limit");
        return;
      }
    } else {
      safeToast.error("Invalid file type. Please select an image or audio file");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setSelectedImage(file);
    };
    reader.readAsDataURL(file);
    
    event.target.value = "";
  };

  // Remove selected image
  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle menu
  const handleMenuOpen = (event, message) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedMessage(message);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEditClick = () => {
    if (selectedMessage) {
      setEditMessageText(selectedMessage.message);
      setEditDialogOpen(true);
      setMenuAnchor(null);
    }
  };

  const handleDeleteClick = () => {
    setMenuAnchor(null);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!selectedMessage || deleting) return;

    setDeleting(true);
    try {
      await chatService.deleteMessage(selectedMessage.messageId);
      setChatHistory((prev) => prev.filter((m) => m.messageId !== selectedMessage.messageId));
      
      if (isConnected && connection) {
        try {
          await signalRManager.deleteMessage(selectedMessage.messageId);
        } catch (signalRError) {
          console.warn("SignalR broadcast failed:", signalRError);
        }
      }
      
      setSelectedMessage(null);
      setDeleteDialogOpen(false);
    } catch (error) {
      safeToast.error(error.response?.data?.error || "Failed to delete message");
    } finally {
      setDeleting(false);
    }
  };

  // Confirm edit
  const handleConfirmEdit = async () => {
    if (!selectedMessage || !editMessageText.trim() || editing) return;

    if (editMessageText.trim() === selectedMessage.message) {
      setEditDialogOpen(false);
      setEditMessageText("");
      setSelectedMessage(null);
      return;
    }

    setEditing(true);
    try {
      const updated = await chatService.updateMessage(selectedMessage.messageId, editMessageText.trim());
      setChatHistory((prev) =>
        prev.map((m) => (m.messageId === updated.messageId ? updated : m))
      );
      
      if (isConnected && connection) {
        try {
          await signalRManager.updateMessage(selectedMessage.messageId, editMessageText.trim());
        } catch (signalRError) {
          console.warn("SignalR broadcast failed:", signalRError);
        }
      }
      
      setEditMessageText("");
      setSelectedMessage(null);
      setEditDialogOpen(false);
    } catch (error) {
      safeToast.error(error.response?.data?.error || "Failed to update message");
    } finally {
      setEditing(false);
    }
  };

  // Handle typing
  const handleTyping = useCallback(() => {
    if (!isConnected) return;

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    startTyping();

    const timeout = setTimeout(() => {
      stopTyping();
      setTypingTimeout(null);
    }, 3000);

    setTypingTimeout(timeout);
  }, [isConnected, typingTimeout, startTyping, stopTyping]);

  // Start recording voice message
  const handleStartRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        safeToast.error("Microphone access is not supported in your browser");
        return;
      }

      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioRecorderRef.current = audioStream;

      const recorder = new MediaRecorder(audioStream);
      const chunks = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: "audio/webm" });
        
        // Create audio file
        const audioFile = new File([audioBlob], "voice-message.webm", { type: "audio/webm" });
        
        // Show preview and allow sending
        const reader = new FileReader();
        reader.onloadend = () => {
          setSelectedImage(audioFile); // Reuse selectedImage state for audio
          setImagePreview(reader.result); // Reuse preview for audio URL
        };
        reader.readAsDataURL(audioBlob);

        // Stop all tracks
        audioStream.getTracks().forEach((track) => track.stop());
        setAudioChunks([]);
        setIsRecording(false);
        setRecordingTime(0);
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
        }
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
      setAudioChunks(chunks);

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      safeToast.error("Failed to access microphone. Please check permissions.");
    }
  };

  // Stop recording voice message
  const handleStopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
  };

  // Cancel recording
  const handleCancelRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }
    if (audioRecorderRef.current) {
      audioRecorderRef.current.getTracks().forEach((track) => track.stop());
    }
    setAudioChunks([]);
    setIsRecording(false);
    setRecordingTime(0);
    setSelectedImage(null);
    setImagePreview(null);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
  };

  // Format recording time
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Cleanup audio recorder on unmount
  useEffect(() => {
    return () => {
      if (audioRecorderRef.current) {
        audioRecorderRef.current.getTracks().forEach((track) => track.stop());
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      Object.values(audioRefs.current).forEach((audio) => {
        if (audio) {
          audio.pause();
          audio.src = "";
        }
      });
    };
  }, []);

  // Cleanup typing timeout
  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
        stopTyping();
      }
    };
  }, [typingTimeout, stopTyping]);

  // Get user image URL
  const getUserImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith("http")) return imageUrl;
    if (imageUrl.startsWith("/uploads/")) {
      return getFullUrl(imageUrl);
    }
    return getFullUrl(`/uploads/${imageUrl}`);
  };

  // Get typing users (excluding current admin)
  const getTypingUsers = useCallback(() => {
    if (!typingUsers || typingUsers.length === 0) return [];
    // For admin, show all typing users (admin doesn't have studentId to filter)
    return typingUsers;
  }, [typingUsers]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          height: fullScreen ? "100%" : "90vh",
          maxHeight: fullScreen ? "100%" : "90vh",
          m: fullScreen ? 0 : 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: isConnected ? "success.main" : "warning.main",
            }}
          />
          <Typography variant="h6">Global Chat</Typography>
          <Chip
            label={isConnected ? "Live" : "Connecting"}
            size="small"
            color={isConnected ? "success" : "warning"}
            sx={{ height: 20, fontSize: "0.65rem" }}
          />
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Grid container sx={{ height: "100%", flexWrap: "nowrap" }}>
          {/* Main Chat Area */}
          <Grid item xs={12} md={8} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Card
              elevation={0}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                border: "none",
                borderRadius: 0,
                height: "100%",
              }}
            >
              {/* Messages Container */}
              <Box
                ref={messagesContainerRef}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  p: 2,
                  bgcolor: alpha("#f5f5f5", 0.3),
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    bgcolor: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: alpha("#000", 0.2),
                    borderRadius: "3px",
                    "&:hover": {
                      bgcolor: alpha("#000", 0.3),
                    },
                  },
                }}
              >
                {loading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress size={24} />
                  </Box>
                ) : chatHistory.length === 0 ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                      No messages yet. Start the conversation!
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {totalMessages > chatHistory.length && (
                      <Box textAlign="center" mb={1.5}>
                        <MDButton
                          variant="text"
                          size="small"
                          onClick={loadMoreMessages}
                          disabled={loadingMore}
                          sx={{ fontSize: "0.75rem", textTransform: "none", minWidth: "auto", px: 2 }}
                        >
                          {loadingMore ? <CircularProgress size={16} /> : "Load older messages"}
                        </MDButton>
                      </Box>
                    )}
                    {chatHistory.map((msg, index) => {
                      const imageUrl = getUserImageUrl(msg.profileImageUrl);
                      const prevMessage = chatHistory[index - 1];
                      const showAvatar =
                        !prevMessage ||
                        (prevMessage.isStaff !== msg.isStaff) ||
                        (prevMessage.isStaff && msg.isStaff && prevMessage.staffId !== msg.staffId) ||
                        (!prevMessage.isStaff && !msg.isStaff && prevMessage.studentId !== msg.studentId) ||
                        new Date(msg.sentDateTime).getTime() -
                          new Date(prevMessage.sentDateTime).getTime() >
                          300000;
                      
                      const showDateSeparator = !prevMessage || !isSameDay(msg.sentDateTime, prevMessage.sentDateTime);
                      // Check if message is from admin using isStaff flag or staffId comparison
                      const isAdminMessage = msg.isStaff || (adminInfo && msg.staffId && adminInfo.staffId === msg.staffId) || (adminInfo && msg.studentName === adminInfo.staffCode);
                      // Admin can edit/delete any message, so always show menu for admin
                      const canEditDelete = true; // Admin has full permissions

                      return (
                        <React.Fragment key={msg.messageId || index}>
                          {showDateSeparator && (
                            <Box display="flex" justifyContent="center" alignItems="center" my={1.5} sx={{ width: "100%" }}>
                              <Chip
                                label={formatDateHeader(msg.sentDateTime)}
                                size="small"
                                sx={{
                                  fontSize: "0.6875rem",
                                  height: 24,
                                  bgcolor: "background.default",
                                  color: "text.secondary",
                                  fontWeight: 500,
                                  border: "none",
                                  "& .MuiChip-label": { px: 1.5 },
                                }}
                              />
                            </Box>
                          )}
                          <Box
                            display="flex"
                            gap={0.75}
                            mb={showAvatar ? 1 : 0.4}
                            sx={{
                              flexDirection: isAdminMessage ? "row-reverse" : "row",
                            }}
                          >
                            <Box sx={{ width: showAvatar ? 28 : 0, transition: "width 0.2s" }}>
                              {showAvatar && (
                                <Avatar
                                  src={imageUrl}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    fontSize: "0.7rem",
                                    border: "1.5px solid",
                                    borderColor: "background.paper",
                                  }}
                                >
                                  {(msg.studentName || msg.userName || "?").charAt(0).toUpperCase()}
                                </Avatar>
                              )}
                            </Box>

                            <Box
                              sx={{
                                flex: 1,
                                maxWidth: isAdminMessage ? "75%" : "85%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: isAdminMessage ? "flex-end" : "flex-start",
                              }}
                            >
                              {showAvatar && !isAdminMessage && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.65rem",
                                    fontWeight: 600,
                                    color: "text.primary",
                                    mb: 0.2,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.4,
                                  }}
                                >
                                  {msg.studentName || msg.userName || "Anonymous"}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{ fontSize: "0.6rem", fontWeight: 400, color: "text.secondary" }}
                                  >
                                    {formatTime(msg.sentDateTime)}
                                  </Typography>
                                </Typography>
                              )}

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 0.5,
                                  position: "relative",
                                  width: "100%",
                                  "&:hover .message-actions": {
                                    opacity: 1,
                                    visibility: "visible",
                                  },
                                }}
                              >
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 0.75,
                                    px: 1,
                                    bgcolor: isAdminMessage ? "primary.main" : "background.paper",
                                    color: isAdminMessage ? "primary.contrastText" : "text.primary",
                                    borderRadius: isAdminMessage ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                                    border: isAdminMessage ? "none" : "1px solid",
                                    borderColor: isAdminMessage ? "transparent" : "divider",
                                    maxWidth: "100%",
                                    wordBreak: "break-word",
                                    display: "inline-block",
                                    flex: 1,
                                  }}
                                >
                                  {msg.imageUrl && (
                                    <Box sx={{ mb: msg.message ? 0.75 : 0 }}>
                                      <img
                                        src={getFullUrl(msg.imageUrl)}
                                        alt="Shared image"
                                        style={{
                                          maxWidth: "100%",
                                          maxHeight: "300px",
                                          borderRadius: "8px",
                                          display: "block",
                                          cursor: "pointer",
                                        }}
                                        onClick={() => window.open(getFullUrl(msg.imageUrl), "_blank")}
                                        onError={(e) => {
                                          e.target.style.display = "none";
                                        }}
                                      />
                                    </Box>
                                  )}
                                  {msg.audioUrl && (
                                    <Box sx={{ mb: msg.message ? 0.75 : 0, minWidth: "200px", maxWidth: "100%" }}>
                                      <audio
                                        src={getFullUrl(msg.audioUrl)}
                                        controls
                                        style={{ width: "100%", height: "32px" }}
                                      />
                                    </Box>
                                  )}
                                  {msg.message && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.8125rem",
                                        lineHeight: 1.4,
                                        whiteSpace: "pre-wrap",
                                      }}
                                    >
                                      {msg.message}
                                    </Typography>
                                  )}
                                </Paper>
                                {canEditDelete && (
                                  <IconButton
                                    size="small"
                                    className="message-actions"
                                    onClick={(e) => handleMenuOpen(e, msg)}
                                    sx={{
                                      opacity: 0.7,
                                      visibility: "visible",
                                      transition: "opacity 0.2s",
                                      p: 0.5,
                                      minWidth: "auto",
                                      width: 24,
                                      height: 24,
                                      color: "rgba(0, 0, 0, 0.7)",
                                      "&:hover": {
                                        opacity: 1,
                                        bgcolor: "rgba(0, 0, 0, 0.08)",
                                      },
                                    }}
                                  >
                                    <MoreVertIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                )}
                              </Box>

                              {showAvatar && isAdminMessage && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.6rem",
                                    color: "text.secondary",
                                    mt: 0.2,
                                    mr: 0.4,
                                  }}
                                >
                                  {formatDate(msg.sentDateTime)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </React.Fragment>
                      );
                    })}

                    {/* Typing Indicator */}
                    {(() => {
                      const typingUsersList = getTypingUsers();
                      if (!typingUsersList || typingUsersList.length === 0) return null;
                      
                      return (
                        <Box display="flex" gap={1} mb={1.5} sx={{ flexDirection: "row" }}>
                          <Box sx={{ width: 32 }}>
                            <Avatar
                              src={getUserImageUrl(typingUsersList[0].profileImageUrl)}
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: "0.75rem",
                                border: "2px solid",
                                borderColor: "background.paper",
                              }}
                            >
                              {(typingUsersList[0].studentName || typingUsersList[0].userName || "?").charAt(0).toUpperCase()}
                            </Avatar>
                          </Box>
                          <Box sx={{ flex: 1, maxWidth: "85%", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                            {typingUsersList.length === 1 && (
                              <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 600, color: "text.primary", mb: 0.25 }}>
                                {typingUsersList[0].studentName || typingUsersList[0].userName || "Anonymous"}
                              </Typography>
                            )}
                            <Paper
                              elevation={0}
                              sx={{
                                p: 1.5,
                                px: 2,
                                bgcolor: "background.paper",
                                borderRadius: "18px 18px 18px 4px",
                                border: "1px solid",
                                borderColor: "divider",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Box
                                display="flex"
                                gap={0.3}
                                alignItems="center"
                                sx={{
                                  "& span": {
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    bgcolor: "text.secondary",
                                    display: "inline-block",
                                    animation: `${typingAnimation} 1.4s infinite ease-in-out`,
                                    "&:nth-of-type(1)": { animationDelay: "0s" },
                                    "&:nth-of-type(2)": { animationDelay: "0.2s" },
                                    "&:nth-of-type(3)": { animationDelay: "0.4s" },
                                  },
                                }}
                              >
                                <span />
                                <span />
                                <span />
                              </Box>
                            </Paper>
                          </Box>
                        </Box>
                      );
                    })()}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </Box>

              {/* Message Input */}
              <Box
                sx={{
                  p: 1.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                {imagePreview && selectedImage && (
                  <>
                    {selectedImage.type?.startsWith("audio/") ? (
                      <Box
                        sx={{
                          mb: 1,
                          p: 1.5,
                          borderRadius: "8px",
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: "background.default",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            bgcolor: "primary.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <MicIcon />
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body2" sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
                            Voice Message
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                            {selectedImage.name || "Recording"}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={handleRemoveImage}
                          sx={{ color: "text.secondary" }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          mb: 1,
                          position: "relative",
                          display: "inline-block",
                          borderRadius: "8px",
                          overflow: "hidden",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{ maxWidth: "200px", maxHeight: "200px", display: "block" }}
                        />
                        <IconButton
                          size="small"
                          onClick={handleRemoveImage}
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            bgcolor: "rgba(0, 0, 0, 0.5)",
                            color: "white",
                            "&:hover": { bgcolor: "rgba(0, 0, 0, 0.7)" },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </>
                )}
                <Box display="flex" gap={1} alignItems="flex-end">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*,audio/*"
                    onChange={handleImageSelect}
                    style={{ display: "none" }}
                  />
                  <IconButton
                    color="default"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!isConnected || sending || uploadingImage || isRecording}
                    sx={{ color: "text.secondary" }}
                    title="Choose from gallery"
                  >
                    <ImageIcon />
                  </IconButton>
                  {!isRecording ? (
                    <IconButton
                      color="default"
                      onClick={handleStartRecording}
                      disabled={!isConnected || sending || uploadingImage || isRecording}
                      sx={{ color: "text.secondary" }}
                      title="Record voice message"
                    >
                      <MicIcon />
                    </IconButton>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1} sx={{ px: 1.5, py: 0.5, borderRadius: 2, bgcolor: "error.lighter" }}>
                      <FiberManualRecordIcon sx={{ fontSize: 16, color: "error.main", animation: `${typingAnimation} 1s infinite` }} />
                      <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "error.main", fontWeight: 600 }}>
                        {formatRecordingTime(recordingTime)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleStopRecording}
                        sx={{ p: 0.5, color: "error.main" }}
                        title="Stop recording"
                      >
                        <StopIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancelRecording}
                        sx={{ p: 0.5, color: "error.main" }}
                        title="Cancel recording"
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  )}
                  <TextField
                    ref={inputRef}
                    fullWidth
                    placeholder={isConnected ? "Type a message..." : "Connecting..."}
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      handleTyping();
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={!isConnected || sending || uploadingImage || isRecording}
                    size="small"
                    multiline
                    maxRows={4}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "background.default",
                        fontSize: "0.875rem",
                        "& fieldset": { borderColor: "divider" },
                        "&:hover fieldset": { borderColor: "primary.main" },
                        "&.Mui-focused fieldset": { borderColor: "primary.main" },
                      },
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={
                      !isConnected ||
                      sending ||
                      uploadingImage ||
                      isRecording ||
                      (!message.trim() && !selectedImage)
                    }
                    sx={{
                      bgcolor: "primary.main",
                      color: "white",
                      "&:hover": { bgcolor: "primary.dark", color: "white" },
                      "&:disabled": {
                        bgcolor: "action.disabledBackground",
                        color: "action.disabled",
                      },
                      p: 1.25,
                      "& svg": { color: "white !important" },
                    }}
                  >
                    {sending || uploadingImage ? (
                      <CircularProgress size={18} sx={{ color: "white !important" }} />
                    ) : (
                      <SendIcon sx={{ fontSize: 18, color: "white !important" }} />
                    )}
                  </IconButton>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Sidebar - Online Users & Stats */}
          <Grid item xs={12} md={4} sx={{ display: { xs: "none", md: "flex" }, flexDirection: "column", gap: 2, p: 2, borderLeft: "1px solid", borderColor: "divider" }}>
            {/* Online Users - Compact View */}
            <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider" }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderBottom: "1px solid", 
                  borderColor: "divider", 
                  bgcolor: "background.paper",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                  transition: "background-color 0.2s",
                }}
                onClick={handleOpenOnlineUsersModal}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    <PeopleIcon sx={{ fontSize: 14 }} />
                    Online · {onlineUsers.length}
                  </Typography>
                  <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                </Box>
              </Box>
            </Card>

            {/* Chat Stats */}
            {chatStats && (
              <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider" }}>
                <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Statistics
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5 }}>
                  <Box display="flex" flexDirection="column" gap={1.5}>
                    <Box>
                      <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary", display: "block", mb: 0.25 }}>
                        Messages
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
                        {chatStats.totalMessages || 0}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box>
                      <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary", display: "block", mb: 0.25 }}>
                        Users
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
                        {chatStats.totalRegisteredUsers || 0}
                      </Typography>
                    </Box>
                    {chatStats.lastMessageDateTime && (
                      <>
                        <Divider sx={{ my: 0.5 }} />
                        <Box>
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary", display: "block", mb: 0.25 }}>
                            Last activity
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
                            {formatDate(chatStats.lastMessageDateTime)}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              </Card>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      {/* Message Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {!selectedMessage?.imageUrl && !selectedMessage?.audioUrl && (
          <MenuItem onClick={handleEditClick}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography sx={{ fontSize: "0.8125rem" }}>Edit</Typography>
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>
            <Typography color="error" sx={{ fontSize: "0.8125rem" }}>Delete</Typography>
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit Message Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Edit Message</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={4}
            value={editMessageText}
            onChange={(e) => setEditMessageText(e.target.value)}
            disabled={editing}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editing} variant="outlined">
            Cancel
          </Button>
          <MDButton
            onClick={handleConfirmEdit}
            color="primary"
            variant="gradient"
            disabled={!editMessageText.trim() || editing}
          >
            {editing ? "Updating..." : "Update"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Delete Message?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this message? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting} variant="outlined">
            Cancel
          </Button>
          <MDButton
            onClick={handleConfirmDelete}
            color="error"
            variant="gradient"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? "Deleting..." : "Delete"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Online Users Modal with Pagination */}
      <Dialog
        open={onlineUsersModalOpen}
        onClose={handleCloseOnlineUsersModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box display="flex" alignItems="center" gap={1}>
            <PeopleIcon sx={{ color: "primary.main", fontSize: 24 }} />
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Online Users
            </Typography>
            <Chip 
              label={onlineUsersTotalCount} 
              size="small" 
              color="primary" 
              sx={{ ml: 1, fontWeight: 600 }}
            />
          </Box>
          <IconButton onClick={handleCloseOnlineUsersModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, flex: 1, display: "flex", flexDirection: "column", minHeight: 400 }}>
          {loadingOnlineUsers ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <CircularProgress />
            </Box>
          ) : allOnlineUsers.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <Typography variant="body2" color="text.secondary">
                No users online
              </Typography>
            </Box>
          ) : (
            <>
              <List sx={{ flex: 1, overflowY: "auto", p: 1 }}>
                {allOnlineUsers.map((user) => {
                  const imageUrl = getUserImageUrl(user.profileImageUrl);
                  return (
                    <ListItem
                      key={user.studentId || user.staffId}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          badgeContent={<FiberManualRecordIcon sx={{ fontSize: 10, color: "success.main" }} />}
                        >
                          <Avatar
                            src={imageUrl}
                            sx={{
                              width: 40,
                              height: 40,
                              border: "2px solid",
                              borderColor: "background.paper",
                            }}
                          >
                            {(user.studentName || user.userName || "?").charAt(0).toUpperCase()}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {user.studentName || user.userName || "Anonymous"}
                            {user.isStaff && (
                              <Chip 
                                label="Staff" 
                                size="small" 
                                color="info" 
                                sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
                              />
                            )}
                          </Typography>
                        }
                        secondary={
                          user.userName && user.studentName ? (
                            <Typography variant="caption" color="text.secondary">
                              @{user.userName}
                            </Typography>
                          ) : null
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
              {onlineUsersTotalPages > 1 && (
                <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", display: "flex", justifyContent: "center" }}>
                  <Pagination
                    count={onlineUsersTotalPages}
                    page={onlineUsersPage}
                    onChange={handleOnlineUsersPageChange}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

// Typechecking props for AdminGlobalChatDialog
AdminGlobalChatDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminGlobalChatDialog;
